/**
 * Auth-aware video list helpers for UI consumers (Preview, patient page, etc.).
 */
import { getApiBaseUrlsForAuth } from "../config/apiBaseUrlsConstants";
import { AUTH_SOURCE } from "../constants/authSource";
import { getVideos } from "./API_SERVICE_VIDEO";
import {
  listCreVideos,
} from "./API_SERVICE_CRE_VIDEO";

function mapLegacyVideo(video) {
  return {
    vid: video.vid,
    longUrl: video.longUrl,
    createdAt: video.createdAt,
    isIntro: Boolean(video.isIntro),
    streamId: video.streamId,
    status: video.status,
  };
}

/**
 * CRE intro-video identification is NOT documented in CRE_Video_API_Integration_Guide.
 * Legacy Zikaron uses an explicit `isIntro` flag from VideoMicroservice.
 * Until Stanis/Vigen confirm CRE semantics, null studyId is only a heuristic candidate.
 */
const CRE_INTRO_VIDEO_HEURISTIC = "UNCONFIRMED_NULL_STUDY_ID";

function mapCreVideo(item, { introCandidate = false } = {}) {
  return {
    vid: item.vid,
    longUrl: item.finalUrl || null,
    captionUrl: item.captionUrl || null,
    makeCaptions: Boolean(item.makeCaptions),
    shareableUrl: item.shareableUrl || null,
    createdAt: item.completedAt || item.requestedAt,
    // Never infer intro on study-scoped list items.
    isIntro: introCandidate,
    introIdentification: introCandidate ? CRE_INTRO_VIDEO_HEURISTIC : null,
    streamId: item.streamId,
    status: item.status,
    errorMessage: item.errorMessage,
  };
}

function buildCrePublicVideoUrl(vid) {
  if (!vid) return null;
  const base = getApiBaseUrlsForAuth(AUTH_SOURCE.CRE).reporting.replace(
    /\/$/,
    "",
  );
  return `${base}/api/Video/${vid}`;
}

export function resolveShareUrl(video, { useCreApi } = {}) {
  if (!video) return null;
  if (useCreApi) {
    return video.shareableUrl || buildCrePublicVideoUrl(video.vid) || null;
  }
  return video.longUrl || null;
}

/** Study-scoped paged list — does not classify intro videos. */
async function fetchCreStudyVideos(
  studyId,
  { pageIndex = 0, pageSize = 20 } = {},
) {
  const studyResponse = await listCreVideos({ studyId, pageIndex, pageSize });
  const studyItems = (Array.isArray(studyResponse?.payload)
    ? studyResponse.payload
    : []
  ).map((item) => mapCreVideo(item));

  return {
    success: Boolean(studyResponse?.success),
    payload: studyItems,
    pageIndex,
    pageSize,
    hasMore: studyItems.length >= pageSize,
  };
}

/**
 * Optional intro candidates — HEURISTIC ONLY (null studyId).
 * Requires product/backend confirmation before treating as intro videos.
 */
async function fetchCreIntroVideoCandidates({ pageSize = 20 } = {}) {
  const response = await listCreVideos({ pageIndex: 0, pageSize });
  const items = (Array.isArray(response?.payload) ? response.payload : [])
    .filter((item) => !item.studyId)
    .map((item) => mapCreVideo(item, { introCandidate: true }));

  return {
    success: Boolean(response?.success),
    payload: items,
    identification: CRE_INTRO_VIDEO_HEURISTIC,
  };
}

/** Fetch CRE study page plus optional intro candidates (kept separate). */
async function fetchCreVideosForStudy(
  studyId,
  { pageIndex = 0, pageSize = 20, includeIntroCandidates = false } = {},
) {
  const studyResult = await fetchCreStudyVideos(studyId, { pageIndex, pageSize });

  if (!includeIntroCandidates) {
    return studyResult;
  }

  const introResult = await fetchCreIntroVideoCandidates({ pageSize });
  const merged = new Map();
  for (const item of [...introResult.payload, ...studyResult.payload]) {
    const key = item.streamId || item.vid;
    if (key) merged.set(key, item);
  }

  return {
    ...studyResult,
    payload: [...merged.values()],
    introIdentification: CRE_INTRO_VIDEO_HEURISTIC,
  };
}

/**
 * Fetch videos for a study using the correct backend for the auth channel.
 */
export async function fetchStudyVideos(
  studyId,
  {
    useCreApi = false,
    pageIndex = 0,
    pageSize = 50,
    includeIntroCandidates = false,
  } = {},
) {
  if (useCreApi) {
    return fetchCreVideosForStudy(studyId, {
      pageIndex,
      pageSize,
      includeIntroCandidates,
    });
  }

  const response = await getVideos(studyId);
  const items = Array.isArray(response?.payload) ? response.payload : [];
  return {
    success: Boolean(response?.success),
    payload: items.map(mapLegacyVideo),
    pageIndex: 0,
    pageSize: items.length,
    hasMore: false,
  };
}

